// The renderer's reader for layout v2 — a faithful mirror of the backend's
// `chart_engine/layout_v2.py`, which owns the same shape server-side.
//
// Two languages hold this because the two halves do different jobs: the backend
// AUTHORS and stores layouts, the browser DRAWS them, and a board arrives here
// as whatever was saved — a v1 flat tile list for every board built so far, a
// region tree for anything authored since. Normalizing on the server would not
// help: the dashboards API still speaks v1 (deliberately — see `to_tiles`), so
// the browser would still receive a list and still have to read both shapes.
// The rules below are therefore kept deliberately boring and identical to the
// Python: same key precedence, same coercions, same fallbacks.
//
// The shim is the load-bearing part. A v1 list becomes ONE `grid12` region
// carrying the original x/y/w/h untouched — never re-derived into relative
// regions — so a board someone arranged by hand draws exactly as they left it.

export const LAYOUT_VERSION = 2;

export const LAYOUT_STACK = 'stack';
export const LAYOUT_GRID = 'grid';
export const LAYOUT_SPLIT = 'split';
export const LAYOUT_FLOW = 'flow';
/** A `tabs` region's children are VIEWS: only the active one renders, and each
 *  carries an `id` + `title` for its tab. This is where a multi-view board lives
 *  now — it used to live inside JSX a model authored, which is why moving a tile
 *  between tabs meant regenerating the page and why tabs were invisible to the
 *  layout editor. */
export const LAYOUT_TABS = 'tabs';
/** Carries a v1 board's original geometry. Nothing new should author it. */
export const LAYOUT_GRID12 = 'grid12';
export const LAYOUTS = [LAYOUT_STACK, LAYOUT_GRID, LAYOUT_SPLIT, LAYOUT_FLOW, LAYOUT_TABS, LAYOUT_GRID12] as const;
export type LayoutKind = (typeof LAYOUTS)[number];

/** Semantic heights — the renderer maps them to CSS. No pixel row unit: that
 *  was the 48px grid's job, and it is what made every board share one rhythm. */
export const HEIGHTS = ['short', 'medium', 'large', 'tall'] as const;
/** A region's height: one of the words, or a raw pixel count the Architect
 *  authored. `skills/composition.md` offers "a 420px focal tile beside a
 *  220px rail" as the example of a real composition decision, and the
 *  normaliser used to accept only the words (AG-1964). */
export type RegionHeight = (typeof HEIGHTS)[number] | number;

export const GAPS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
/** The gap words `skills/composition.md` asks for, beside the scale names every
 *  stored board already uses (AG-2111). */
const GAP_ALIAS: Record<string, string> = { tight: 'sm', regular: 'md', generous: 'lg' };
export type RegionGap = (typeof GAPS)[number];

/** A deeply nested tree is almost always a malformed one. */
export const MAX_DEPTH = 8;

/** The keys a legacy tile carries, preserved verbatim by the shim. */
const GRID_KEYS = ['x', 'y', 'w', 'h', 'min_w', 'min_h', 'static'] as const;

const REF_KEYS = ['tile', 'chart_id', 'report_id', 'id', 'i'] as const;

/**
 * What a tile IS to its page, as a word.
 *
 * `dominant | secondary | supporting | detail` are TIERS and their order is the
 * only thing they mean. `rail | wide | compact` say something about a tile's
 * relation to its row rather than its rank.
 *
 * The backend's repair emits these instead of spans (`composition_patterns.py`,
 * AG-2103), because it has never seen the viewport: what a word comes to in
 * columns is computed here, against the grid the board is actually rendering.
 */
export const EMPHASIS = [
  'dominant', 'secondary', 'supporting', 'detail', 'rail', 'wide',
] as const;
export type Emphasis = (typeof EMPHASIS)[number];

/** `skills/visual-language.md` names the same scale `size`, with two of its
 *  tiers spelled differently. One vocabulary, two spellings — mirrors
 *  `layout_v2._EMPHASIS_ALIAS`. */
const EMPHASIS_ALIAS: Record<string, string> = {
  prominent: 'secondary',
  regular: 'supporting',
  // `compact` said exactly what `rail` says — take the least room — and
  // `composition_patterns` already declared both as RELATIONS rather than tiers.
  // Two spellings of one statement is not two semantic values (AG-2171).
  compact: 'rail',
};

/** The emphasis word a node states, or undefined. Shared by both branches of
 *  `region` (AG-2134): a GROUP of related tiles is a member of its row exactly
 *  as a single tile is, and it says so in the same word. */
function emphasisOf(raw: Row): Emphasis | undefined {
  const asked = text(raw.emphasis || raw.size).toLowerCase();
  const word = EMPHASIS_ALIAS[asked] ?? asked;
  return (EMPHASIS as readonly string[]).includes(word) ? (word as Emphasis) : undefined;
}

export const NAV_SEPARATION = ['fill', 'underline', 'outline', 'weight', 'none'] as const;
export const NAV_ENCLOSURE = ['none', 'grouped', 'inset'] as const;
export const NAV_AXIS = ['inline', 'rail'] as const;
/** WHERE THE BAR SITS ACROSS ITS AXIS. The renderer used to answer this with
 *  `align-self: flex-start` and nothing else, so every board in the workspace
 *  put its tabs in the same corner whatever the design said — the one part of
 *  the navigation the Architect could not reach (AG-2176). */
export const NAV_ALIGN = ['start', 'center', 'end', 'justified'] as const;
/**
 * HOW A REGION'S MEMBERS SIT ACROSS ITS AXIS (AG-2246).
 *
 * The hole `NAV_ALIGN` closed for the tab bar, one level up. A grid row is as
 * tall as its tallest member, and the stylesheet decided on its own which
 * members refused to be stretched to it — by asking what FORM they were.
 * `.dt-slot[data-form="single_value"]` was pinned to the top of its row, capped
 * at 44ch wide in a stack, and forced into two columns under 760px. So "holds
 * one number" and "sits at the top of its row and stops at 44ch" were the same
 * statement, and no design could separate them.
 *
 * Absent means absent: the region aligns the way its layout already does, which
 * is the browser's answer rather than a look this renderer picked.
 *
 * Mirrors `layout_v2.REGION_ALIGN`.
 */
export const REGION_ALIGN = ['start', 'center', 'end', 'stretch'] as const;
/** WHETHER THE BAR COMES BEFORE THE VIEW OR AFTER IT. A set of peers you switch
 *  between reads better above; a sequence you step through — a wizard, a
 *  report's sections — reads better under the thing it navigates. */
export const NAV_ORDER = ['before', 'after'] as const;
export type NavSeparation = (typeof NAV_SEPARATION)[number];
export type NavEnclosure = (typeof NAV_ENCLOSURE)[number];
export type NavAlign = (typeof NAV_ALIGN)[number];
export type RegionAlign = (typeof REGION_ALIGN)[number];
export type NavOrder = (typeof NAV_ORDER)[number];
export type NavAxis = (typeof NAV_AXIS)[number];

/** The older spellings of a surface, and the two skills' words for the same
 *  things. Mirrors `design_system.SURFACE_ALIAS` — and is the ONLY copy in the
 *  browser: `theme.ts` imports this one. A test holds it equal to the backend's,
 *  because two overlapping sets is how they drift. */
export const SURFACE_ALIAS: Record<string, string> = {
  transparent: 'none', borderless: 'none', plain: 'none', 'full-bleed': 'none',
  elevated: 'raised', outline: 'inset',
};

/**
 * A tile's own card treatment — mirrors `design_system.tile_style` (AG-2157).
 *
 * THIS WAS MISSING, and it is why every tile on every board rendered as the
 * same card. The backend coerced and stored `style`, `collectTiles` read
 * `node.style`, and `TileCard` branched on `style.card` / `style.transparent` —
 * but this normaliser, which every layout passes through, built its tile out of
 * `tile`, the grid keys, `span`, `emphasis`, `height` and `chart_id` and
 * dropped the rest. So AG-2124's six surfaces reached the browser and were
 * discarded at the door: a board that asked for a bare hero beside an inset
 * table got two identical cards.
 *
 * The tests it had all passed, because they exercised `tile_style` and
 * `cardVars` directly and nothing rendered a styled tile through `normalize`.
 */
function tileStyle(raw: Row): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  const asked = text(raw.surface || raw.card || raw.cardStyle).toLowerCase();
  if (asked) {
    const word = SURFACE_ALIAS[asked] ?? asked;
    // A plain token only — it becomes a data attribute and a class hook.
    if (/^[a-z][a-z0-9-]{0,31}$/.test(word)) {
      out.card = word;
      // A tile that asked for NO surface is not a card at all.
      if (word === 'none') out.transparent = true;
    }
  }
  const accent = text(raw.accent);
  if (accent.startsWith('#') && /^#[0-9a-f]{3,8}$/i.test(accent)) out.accent = accent;
  if (raw.headerless !== undefined && raw.headerless !== null) {
    out.headerless = Boolean(raw.headerless);
  }
  return Object.keys(out).length ? out : undefined;
}

export interface TileRegion {
  tile: string;
  chart_id?: string;
  /** This tile's own card treatment — the surface it asked for, an accent, and
   *  whether it draws a header. Mirrors `design_system.tile_style`. */
  style?: Record<string, unknown>;
  span?: number;
  emphasis?: Emphasis;
  /** How THIS member sits in its row. The rules this replaces were per tile, so
   *  the design speaks at the same grain. */
  align?: RegionAlign;
  height?: RegionHeight;
  x?: number; y?: number; w?: number; h?: number;
  min_w?: number; min_h?: number; static?: boolean;
  [extra: string]: unknown;
}

export interface BranchRegion {
  layout: LayoutKind;
  regions: Region[];
  /** What this GROUP is to its parent — the same seven words a tile uses, and
   *  resolved the same way against the same measured grid (AG-2134). A region
   *  that gathers related tiles is a member of its row like any other. */
  emphasis?: Emphasis;
  /** How this region's members sit across its axis, where it stated one. */
  align?: RegionAlign;
  gap?: RegionGap;
  columns?: number | 'auto-fit';
  ratio?: number[];
  min_item?: string;
  height?: RegionHeight;
  /** THE NAVIGATION'S OWN DESIGN (AG-2154). Set on a `tabs` region: three words
   *  saying what marks the active view, whether the set reads as one object, and
   *  whether it runs along the top or down the side. Every pixel is derived from
   *  the board's own tokens by `navVars` — nothing here is a size. */
  nav?: { separation?: NavSeparation; enclosure?: NavEnclosure; axis?: NavAxis; align?: NavAlign; order?: NavOrder };
  /** Set on a VIEW — a direct child of a `tabs` region. `id` is what an edit
   *  addresses ("add a tile to the SLA tab"); `title` is what the tab bar shows. */
  id?: string;
  title?: string;
  /** THE VIEW'S OWN DESIGN SPEC (AG-2122).
   *
   *  A view used to carry `id` and `title` and nothing else, so there was
   *  nowhere to record WHY it is organised the way it is — and with nowhere to
   *  put it, nothing downstream could tell a designed view from the previous one
   *  with different charts in it.
   *
   *  `purpose` and `narrative` reach the page compiler's brief for this view.
   *  `density` is resolved HERE, per view: a dense operations wall and a
   *  spacious executive summary are different answers to different questions,
   *  and a board-wide density cannot express both.
   *  `compositionReuseReason` is read by the duplication diagnostic — repetition
   *  somebody explained is a decision, repetition nobody explained is a finding.
   *
   *  Mirrors `layout_v2._VIEW_DESIGN_KEYS`. */
  purpose?: string;
  narrative?: string;
  compositionReuseReason?: string;
  density?: 'compact' | 'cozy' | 'comfortable' | 'spacious';
}

export type Region = TileRegion | BranchRegion;

export interface LayoutTree {
  version: number;
  root: BranchRegion;
  /** True when this tree is a shimmed v1 board rather than authored regions. */
  legacy?: boolean;
}

/**
 * The widest a dashboard column grid may be when nothing has measured the board.
 *
 * This used to be `MAX_GRID_COLUMNS = 6`, applied everywhere, and its own
 * comment gave the reasoning away: "six 1fr columns at 1440px is ~220px a tile,
 * which is about where a chart stops being readable". That is a calculation —
 * width over legible width — and freezing its answer made it wrong at every
 * other width, typeface and density (AG-2104).
 *
 * The calculation itself lives in `legibility.columnsThatFit`, and the canvas
 * passes its result in. This bound remains for the callers that genuinely have
 * no container to measure — the layout NORMALISER, which runs server-side data
 * through the same reader with no DOM at all — where refusing to cap would let a
 * region asking for thirteen columns through to a renderer that then has to
 * rescue it. It is deliberately generous: an upper sanity bound, not a design.
 */
export const UNMEASURED_COLUMN_CAP = 12;

/**
 * How many columns a grid region actually gets.
 *
 * The count is the Architect's, and it is taken as authored right up to the point
 * where it would make the board unreadable — which it did, live: a three-tab
 * product-operations board came back with a region asking for THIRTEEN columns
 * over four tiles, so every tile rendered 108px wide and its labels wrapped one
 * character per line. That is not a design anybody chose; it is a number nobody
 * bounded.
 *
 * Two bounds, both structural rather than aesthetic:
 *
 * - **no more columns than there are things to put in them.** Four tiles can
 *   never need thirteen columns, and the extra tracks take width from the tiles
 *   that exist. A child that spans more than one column is counted for its span,
 *   so a deliberate 2-up-plus-wide-hero still gets the tracks it needs.
 * - **a ceiling**, because even with enough tiles there is a width past which a
 *   column says nothing.
 *
 * Anything unusable — zero, a negative, a non-number, `auto-fit` — returns null,
 * and the caller falls back to `auto-fit`, which sizes itself.
 */
export function gridColumns(
  asked: unknown, regions: Region[] = [], fit: number | null = null,
): number | null {
  if (typeof asked !== 'number' || !Number.isFinite(asked) || asked < 1) return null;
  const children = regions.length;
  if (!children) return null;
  const spans = regions.map((region) => {
    const span = isTileRegion(region) && typeof region.span === 'number' ? region.span : 1;
    return Number.isFinite(span) && span > 0 ? Math.floor(span) : 1;
  });

  // REDUCE THE GRID TO THE SMALLEST ONE THAT STILL EXPRESSES IT.
  //
  // A 12-column region holding three tiles of `span: 6` is not a 12-column
  // layout and not a 3-column one: it is "halves", and halves need exactly two
  // columns. Dividing the asked count and every span by their common divisor
  // says that exactly — twelve-over-six is two, and each tile becomes one
  // column wide, so two sit per row as authored.
  //
  // Choosing by tile COUNT instead is what kept the boards flat. Three halves
  // in a 3-column grid leaves each tile spanning 2 of 3, and two of those no
  // longer fit on a row, so every tile took a row of its own — which is what
  // the live boards did.
  const divisor = spans.concat(Math.floor(asked)).reduce(greatestCommonDivisor);
  const reduced = Math.floor(asked) / divisor;
  const widestReduced = Math.max(...spans.map((span) => span / divisor), 1);

  // Still no more columns than the region can fill: a 13-column grid over four
  // unit tiles is 108px slivers with labels wrapping one character per line,
  // and a tile wider than one column is counted for the width it needs.
  //
  // `fit` is how many columns the MEASURED container can actually hold at this
  // board's type scale (`legibility.columnsThatFit`). When the caller has a
  // container it is the real bound, and it moves with the viewport, the density
  // and the typeface; when it has none — the normaliser, a test — the generous
  // sanity cap applies instead.
  const bound = (typeof fit === 'number' && Number.isFinite(fit) && fit >= 1)
    ? Math.floor(fit)
    : UNMEASURED_COLUMN_CAP;
  // A ROW STATING WORDS NEEDS THE TRACKS ITS WORDS NEED (AG-2136). The cap is
  // "no more columns than the region can fill", which is right for unit-span
  // tiles and wrong for tiles that stated a RATIO instead of a span: an authored
  // twelve-column row of a `dominant` beside a `detail` reduced to two columns,
  // where four-to-one can only come out as one-to-one.
  const needed = emphasisTracks(regions.map((r) => r.emphasis));
  const capped = Math.min(reduced, Math.max(children, widestReduced, needed), bound);
  return Math.max(1, Math.floor(capped));
}

function greatestCommonDivisor(a: number, b: number): number {
  let x = Math.abs(Math.floor(a));
  let y = Math.abs(Math.floor(b));
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

/**
 * A tile's span, expressed on the grid the board will ACTUALLY render.
 *
 * `gridColumns` bounds the Architect's column count, and until this existed the
 * spans were left on the count it had been bounded from — so the two halves of
 * one layout disagreed about what a column was. A KPI row authored as four
 * tiles of `span: 3` in a 12-column grid had its grid clamped to 4 columns and
 * kept `span: 3`: a quarter-width tile became three-quarters, one tile per row,
 * and every board in the product came out as a single stacked column whatever
 * its design said. Both boards read off live output on 2026-09-12 looked like
 * that, and the layout data underneath was correct the whole time.
 *
 * So the span is rescaled by the same ratio the columns were: a quarter of
 * twelve stays a quarter of four. Rounding keeps it at least one column and
 * never wider than the grid, because a span nobody can fit is how the flatten
 * happened in the first place.
 *
 * With no fixed count (`auto-fit` sizes itself) there is no scale to convert
 * against, so the span is merely bounded — an unbounded one forces the grid to
 * grow tracks for it and pushes the row off the right edge.
 */
export function tileSpan(span: unknown, asked: unknown, actual: number | null): number | null {
  if (typeof span !== 'number' || !Number.isFinite(span) || span < 1) return null;
  const want = Math.floor(span);
  if (actual === null) return Math.min(want, UNMEASURED_COLUMN_CAP);
  const authored = (typeof asked === 'number' && Number.isFinite(asked) && asked >= 1)
    ? Math.floor(asked)
    : actual;
  if (authored === actual) return Math.max(1, Math.min(want, actual));
  const scaled = Math.round((want * actual) / authored);
  return Math.max(1, Math.min(scaled, actual));
}

/**
 * A tile's emphasis → its share of the row, as a relative WEIGHT.
 *
 * These are ranks, not sizes. The scale says a `dominant` tile takes twice the
 * room of a `supporting` one and four times a `detail`; it does not say how many
 * pixels any of that is, and it cannot — `fillGridRows` turns the weights into
 * whole columns of the grid the board is measured to hold, so the same words are
 * a different geometry at 900px and at 2560px.
 *
 * `wide` is the exception, and deliberately so: it is not a rank but a statement
 * that nothing belongs beside this tile, so it takes the row whatever else is in
 * it.
 */
export const EMPHASIS_WEIGHT: Record<Emphasis, number> = {
  dominant: 4,
  secondary: 3,
  supporting: 2,
  detail: 1,
  // NEITHER OF THESE IS A RANK, and neither is resolved by weight — both are
  // answered against the GRID in `emphasisRow`, which is what makes them
  // statements about the row rather than positions in it. `composition_patterns`
  // has always declared them RELATIONS; the resolver used to treat `rail` as a
  // fourth spelling of `detail`, so the vocabulary claimed a distinction it did
  // not have (AG-2171). Their entries here are never read.
  rail: 0,
  wide: 0,
};

/** The words answered against the GRID rather than against the row's ratio.
 *  `wide` takes the row; `rail` takes the least the row can give. */
const RELATIONS = new Set<Emphasis>(['wide', 'rail']);

/**
 * How many tracks a row needs before its own words can say anything (AG-2136).
 *
 * THE DENOMINATOR IS THE WHOLE PROBLEM. A row with no authored column count used
 * to be given one column per child — enough to HOLD them, never enough to
 * express what they said. Three tiles at 4:2:1 on three columns round to
 * [2,1,1]: four columns of demand on a three-column grid, so the last one
 * wrapped and `fillGridRows` grew it to the full width. Measured in Chromium at
 * 1440px, the tile the design called `detail` came out 1156px wide, on a row of
 * its own, above a `dominant` of 767. The hierarchy rendered inverted.
 *
 * The smallest count that states the ratio EXACTLY is the sum of the weights
 * reduced to lowest terms — 4:2:1 needs seven tracks, four equal peers need
 * four — which is the same reduction `gridColumns` already applies to authored
 * spans. Never fewer than one per child, and the caller bounds it by what the
 * measured container can legibly hold.
 */
export function emphasisTracks(words: Array<Emphasis | undefined>): number {
  const weights = words.filter((w) => !(w && RELATIONS.has(w))).map(rowWeight);
  if (!weights.length) return Math.max(1, words.length);
  const divisor = weights.reduce(greatestCommonDivisor);
  const exact = weights.reduce((a, b) => a + b, 0) / (divisor || 1);
  return Math.max(words.length, Math.ceil(exact), 1);
}

/** A member's weight in its row. A member that stated nothing claims the least
 *  any tier claims, rather than nothing at all — the row has to sum to whole
 *  columns, so every member needs a share once any member has one. */
function rowWeight(word: Emphasis | undefined): number {
  return word ? EMPHASIS_WEIGHT[word] : EMPHASIS_WEIGHT.detail;
}

/**
 * A whole ROW of emphasis words → whole columns that sum to EXACTLY the grid it
 * is drawn on, or nulls for a row where nobody ranked anything.
 *
 * Resolved together rather than one member at a time (AG-2136). Independent
 * rounding is what let a row ask for more columns than it had: each share was
 * correct on its own and the row overflowed, wrapped, and `fillGridRows` then
 * grew the orphan to the full width — a rule that is right for a tile PRUNED out
 * of a row (AG-2075) and exactly wrong for a tile the design meant to place.
 *
 * Proportional to the weights PRESENT in the row rather than to the whole
 * vocabulary: three `supporting` tiles share the row equally, and a `dominant`
 * beside one `detail` takes the larger part of it. That is what makes the words
 * relative — the same word is a different width in different company, which is
 * the difference between a vocabulary and a table of sizes.
 *
 * WHERE THE RATIO WILL NOT FIT, THE ANSWER IS EQUAL. Three tiers on three
 * columns is honestly "this board is too narrow to rank them". It is never "the
 * detail is the widest thing on the page".
 */
export function emphasisRow(
  words: Array<Emphasis | undefined>, columns: number,
): Array<number | null> {
  const cols = Math.floor(columns);
  if (!(cols >= 1) || !words.length) return words.map(() => null);
  // A row where nobody said anything has nothing to resolve: CSS gives each
  // member its one column, which is what an unranked row means.
  if (!words.some(Boolean)) return words.map(() => null);
  // A RELATION IS NOT A RANK, so neither is part of the ratio.
  //
  //   `wide` takes the row whatever else is on it.
  //   `rail` takes the LEAST the row can give — one track — and does not grow
  //          with the grid. That is what makes it a rail rather than a
  //          low-ranked peer: at twelve columns a `detail` beside a `dominant`
  //          is two or three tracks and at twenty-four it is five, while a rail
  //          is one at both. It cannot be illegibly thin, because the column
  //          count itself is bounded by what the widest member needs to be
  //          legible (`columnsThatFit`), so one column already clears that.
  //
  // The ranked members then divide what is left, which is how a rail gives its
  // room to the tile it accompanies instead of taking a proportional share.
  const railCols = words.reduce((n, w) => n + (w === 'rail' ? 1 : 0), 0);
  const ranked = words.map((w, i) => (w && RELATIONS.has(w) ? -1 : i));
  const members = ranked.filter((i) => i >= 0);
  const forRanked = Math.max(members.length, cols - railCols);
  const shares = allocate(members.map((i) => rowWeight(words[i])), forRanked);
  const out = words.map((w) => (w === 'wide' ? cols : w === 'rail' ? 1 : null));
  members.forEach((index, k) => { out[index] = shares[k]; });
  return out;
}

/** Whole columns proportional to `weights`, summing to exactly `cols` — unless
 *  there are more members than columns, where the row genuinely wraps and each
 *  member keeps the one column it cannot go below. */
function allocate(weights: number[], cols: number): number[] {
  const total = weights.reduce((a, b) => a + b, 0);
  if (!total) return weights.map(() => 1);
  const exact = weights.map((w) => (w * cols) / total);
  const out = exact.map((v) => Math.max(1, Math.round(v)));
  const sum = () => out.reduce((a, b) => a + b, 0);
  // Rounding and the floor of one both overshoot. Take from the WIDEST member
  // each time, so the row stays in order as it shrinks and ends up equal rather
  // than inverted when the ratio will not fit.
  while (sum() > cols) {
    let victim = -1;
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] <= 1) continue;
      if (victim < 0 || out[i] > out[victim]
        || (out[i] === out[victim] && weights[i] <= weights[victim])) victim = i;
    }
    if (victim < 0) break;            // more members than columns: it wraps
    out[victim] -= 1;
  }
  // And when it undershoots, the member furthest below its exact share gets it.
  while (sum() < cols) {
    let lucky = 0;
    for (let i = 1; i < out.length; i += 1) {
      const better = (exact[i] - out[i]) - (exact[lucky] - out[lucky]);
      if (better > 0 || (better === 0 && weights[i] > weights[lucky])) lucky = i;
    }
    out[lucky] += 1;
  }
  return out;
}

export function isTileRegion(region: Region): region is TileRegion {
  return typeof (region as TileRegion).tile === 'string' && Boolean((region as TileRegion).tile);
}

type Row = Record<string, unknown>;

function text(value: unknown): string {
  // Mirrors Python's `str(value or "").strip()`: 0, false and null all read as
  // absent, which is what the server's coercion does.
  if (value === null || value === undefined || value === false || value === 0 || value === '') return '';
  return String(value).trim();
}

function isRow(value: unknown): value is Row {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** A tile's chart/report reference, whichever key it carries. */
function tileId(tile: Row): string {
  for (const key of REF_KEYS) {
    const value = text(tile[key]);
    if (value) return value;
  }
  return '';
}

export function isRegionTree(raw: unknown): raw is { root: Row; legacy?: boolean } {
  return isRow(raw) && isRow((raw as Row).root);
}

/** Wrap a v1 tile list as a region tree, geometry untouched. */
export function fromTiles(tiles: unknown): LayoutTree {
  const regions: TileRegion[] = [];
  for (const raw of Array.isArray(tiles) ? tiles : []) {
    if (!isRow(raw)) continue;
    const ref = tileId(raw);
    if (!ref) continue;
    const region: TileRegion = { tile: ref };
    for (const key of GRID_KEYS) {
      if (raw[key] !== null && raw[key] !== undefined) (region as Row)[key] = raw[key];
    }
    // Anything else the tile carried (title overrides, per-tile props) rides
    // along, so the shim never drops board state it does not understand.
    for (const [key, value] of Object.entries(raw)) {
      if (key in region || (REF_KEYS as readonly string[]).includes(key)) continue;
      (region as Row)[key] = value;
    }
    if (region.chart_id === undefined) region.chart_id = ref;
    regions.push(region);
  }
  return { version: LAYOUT_VERSION, root: { layout: LAYOUT_GRID12, regions }, legacy: true };
}

/** Coerce one node — a tile reference or a nested region — else null.
 *
 *  `legacy` says this tree came from the v1 shim, and it is the ONLY thing that
 *  may carry a `grid12` region — mirrors `layout_v2._region` (AG-2110). */
function region(raw: unknown, depth: number, legacy = false): Region | null {
  if (!isRow(raw) || depth > MAX_DEPTH) return null;

  const ref = text(raw.tile);
  const childrenRaw = raw.regions;
  if (ref && !Array.isArray(childrenRaw)) {
    const out: TileRegion = { tile: ref };
    for (const key of GRID_KEYS) {
      if (raw[key] !== null && raw[key] !== undefined) (out as Row)[key] = raw[key];
    }
    const span = raw.span;
    if (typeof span === 'number' && Number.isInteger(span) && span > 0) out.span = span;
    // `size` is the same vocabulary under the name `visual-language.md` gives
    // it; `prominent`/`regular` are that skill's spellings of two of the tiers.
    const emphasis = emphasisOf(raw);
    if (emphasis) out.emphasis = emphasis;
    const height = text(raw.height).toLowerCase();
    if ((HEIGHTS as readonly string[]).includes(height)) out.height = height as RegionHeight;
    else if (typeof raw.height === 'number' && isFinite(raw.height) && raw.height > 0) {
      out.height = raw.height;
    }
    if (raw.chart_id) out.chart_id = text(raw.chart_id);
    // PER-TILE CARD TREATMENT (AG-2157). Read from the leaf itself as well
    // as from a nested `style` object, exactly as the backend does — a
    // board may spell it either way and both must reach the renderer.
    const style = tileStyle(isRow(raw.style) ? (raw.style as Row) : raw);
    if (style) out.style = style;
    return out;
  }

  const children: Region[] = [];
  for (const child of Array.isArray(childrenRaw) ? childrenRaw : []) {
    const coerced = region(child, depth + 1, legacy);
    if (coerced) children.push(coerced);
  }
  // A region with nothing in it is a hole in the page, not a layout.
  if (!children.length) return null;

  let kind = text(raw.layout).toLowerCase();
  if (!(LAYOUTS as readonly string[]).includes(kind)) kind = LAYOUT_STACK;
  // A NEW BOARD IS NEVER AUTHORED ON THE LEGACY GRID (AG-2110). `grid12` carries
  // a v1 board's original x/y/w/h, and the shim is its only producer; an
  // authored one would freeze the board at twelve columns and a 48px row unit,
  // unable to reflow. It becomes a semantic grid, which is what it was reaching
  // for — the spans survive, the geometry is computed against the container.
  if (kind === LAYOUT_GRID12 && !legacy) kind = LAYOUT_GRID;
  // A TAB HOLDING ONE TILE IS STILL A PAGE. `layout_v2._as_view` wraps a bare
  // tile child of `tabs` into a one-tile view; this reader used to leave it a
  // tile, and `TabsView` — which renders views — dropped it. The same board
  // had a tab on the backend and lost content in the browser.
  const views = kind === LAYOUT_TABS
    ? children.map((child): Region => (isTileRegion(child) ? { layout: LAYOUT_STACK, regions: [child] } : child))
    : children;
  const out: BranchRegion = { layout: kind as LayoutKind, regions: views };
  // WHAT THIS GROUP IS TO ITS PARENT (AG-2134). Dropped here, a composition
  // could only ever be a flat list: the moment related tiles were gathered into
  // a region, the group became one nameless child and took a single track
  // beside its full-width siblings.
  const groupEmphasis = emphasisOf(raw);
  if (groupEmphasis) out.emphasis = groupEmphasis;
  const rawGap = text(raw.gap).toLowerCase();
  const gap = GAP_ALIAS[rawGap] ?? rawGap;
  if ((GAPS as readonly string[]).includes(gap)) out.gap = gap as RegionGap;
  const columns = raw.columns;
  if (typeof columns === 'number' && Number.isInteger(columns) && columns > 0) out.columns = columns;
  else if (text(columns).toLowerCase() === 'auto-fit') out.columns = 'auto-fit';
  const ratio = (Array.isArray(raw.ratio) ? raw.ratio : []).filter(
    (r): r is number => typeof r === 'number' && isFinite(r) && r > 0,
  );
  if (ratio.length >= 2) out.ratio = ratio;
  const minItem = text(raw.min_item);
  if (minItem) out.min_item = minItem;
  // The region's own design spec — carried where stated, absent where not.
  for (const key of ['purpose', 'narrative', 'compositionReuseReason'] as const) {
    const value = text(raw[key]);
    if (value) out[key] = value;
  }
  const density = text(raw.density).toLowerCase();
  if (['compact', 'cozy', 'comfortable', 'spacious'].includes(density)) {
    out.density = density as BranchRegion['density'];
  }
  const height = text(raw.height).toLowerCase();
  if ((HEIGHTS as readonly string[]).includes(height)) out.height = height as RegionHeight;
  else if (typeof raw.height === 'number' && isFinite(raw.height) && raw.height > 0) {
    out.height = raw.height;
  }
  // THE NAVIGATION'S OWN DESIGN (AG-2154). Coerced word by word against the
  // vocabularies, so an invented one is dropped and inherits the board's default
  // rather than reaching the renderer as an unknown.
  if (isRow(raw.nav)) {
    const nav: Record<string, string> = {};
    for (const [key, words] of [
      ['separation', NAV_SEPARATION], ['enclosure', NAV_ENCLOSURE], ['axis', NAV_AXIS],
      ['align', NAV_ALIGN], ['order', NAV_ORDER],
    ] as const) {
      const word = text((raw.nav as Row)[key]).toLowerCase();
      if ((words as readonly string[]).includes(word)) nav[key] = word;
    }
    if (Object.keys(nav).length) out.nav = nav as BranchRegion['nav'];
  }
  // A VIEW's identity. Dropped, a tab could not be labelled or addressed.
  const id = text(raw.id);
  if (id) out.id = id;
  const title = text(raw.title);
  if (title) out.title = title;
  return out;
}

/**
 * Any stored layout → the one shape the renderer reads.
 *
 * v1 list → shimmed (geometry preserved); v2 tree → validated and pruned;
 * anything unusable → an empty tree, because a blank board a user can still
 * drag charts into beats an error page.
 */
export function normalize(raw: unknown): LayoutTree {
  if (isRegionTree(raw)) {
    const legacy = Boolean(raw.legacy);
    let root = region(raw.root, 0, legacy) ?? { layout: LAYOUT_STACK, regions: [] };
    // A bare tile at the root is still a page.
    if (isTileRegion(root)) root = { layout: LAYOUT_STACK, regions: [root] };
    const out: LayoutTree = { version: LAYOUT_VERSION, root: root as BranchRegion };
    if (legacy) out.legacy = true;
    return out;
  }
  if (Array.isArray(raw)) return fromTiles(raw);
  if (isRow(raw) && Array.isArray(raw.tiles)) return fromTiles(raw.tiles);
  return { version: LAYOUT_VERSION, root: { layout: LAYOUT_STACK, regions: [] } };
}

/**
 * Add a tile to a region tree — or return null when the layout is not one, so
 * the caller uses the v1 path instead.
 *
 * The two shapes are edited differently and must not be confused: a v1 board is
 * a list the auto-layout service re-flows into grid coordinates, while a tree
 * has no coordinates to compute. Appending a v1 tile to a tree board would
 * replace the arrangement someone authored with a flat list — the exact loss the
 * shim exists to prevent, just in the other direction.
 */
export function appendTileToTree(raw: unknown, chartId: string): LayoutTree | null {
  if (!isRegionTree(raw)) return null;
  const tree = normalize(raw);
  if (tree.legacy) return null;
  return {
    ...tree,
    root: { ...tree.root, regions: [...tree.root.regions, { tile: chartId, chart_id: chartId }] },
  };
}

/** Drop the tile at `index` in page order from a region tree, and any region
 *  left empty by it — a region with nothing in it is a hole in the page, not a
 *  layout. Null when the layout is not a tree. */
export function removeTileFromTree(raw: unknown, index: number): LayoutTree | null {
  if (!isRegionTree(raw)) return null;
  const tree = normalize(raw);
  if (tree.legacy) return null;
  let seen = 0;
  const walk = (node: Region): Region | null => {
    if (isTileRegion(node)) return seen++ === index ? null : node;
    const kept = node.regions.map(walk).filter((r): r is Region => r !== null);
    return kept.length ? { ...node, regions: kept } : null;
  };
  const root = walk(tree.root);
  return {
    ...tree,
    root: (root && !isTileRegion(root) ? root : { layout: LAYOUT_STACK, regions: [] }),
  };
}

/** Every tile referenced by a layout, in page order — what the renderer
 *  resolves, and what tells a caller whether a board is empty. */
export function tileRefs(layout: unknown): string[] {
  const found: string[] = [];
  const walk = (node: Region) => {
    if (isTileRegion(node)) { found.push(node.tile); return; }
    for (const child of node.regions) walk(child);
  };
  walk(normalize(layout).root);
  return found;
}

/**
 * Every row of a grid fills the grid (AG-2075).
 *
 * A view is composed as rows that add up — six scorecards at `span: 2`, then a
 * 7/5 pair, then a full-width strip. Then the build prunes: QA drops a
 * redundant tile, the data gate drops an empty one, a planned tile never builds
 * at all. `prune_missing` removes the tile and nothing reconsiders the ROW it
 * came out of, so five scorecards of span 2 is ten columns of twelve, and a 7
 * whose 5 was pruned sits alone with five columns of nothing beside it.
 *
 * Measured live on `Global Revenue Command Center` (2026-09-15, a 1292px
 * board): 536px, 435px and 652px of empty grid on three separate rows, each
 * traceable to exactly one tile the build had refused.
 *
 * The rule is structural rather than aesthetic, which is why it belongs here
 * and not in a design prompt: a row stopping short of its grid is not an
 * arrangement someone chose, it is the hole left by something removed. Growing
 * the row's tiles in PROPORTION keeps everything the designer did say — 7/5
 * still reads 7:5, a lone tile takes the width — while removing space nobody
 * asked for.
 *
 * It runs at render time against `gridCols`, the count actually on screen, so
 * it holds just as well once a narrow viewport has halved the grid.
 *
 * Rows are packed the way CSS grid auto-placement packs them: left to right,
 * wrapping when the next tile cannot fit in what is left of the row.
 */
export function fillGridRows(
  spans: Array<number | null | undefined>,
  columns: number | null,
): Array<number | null> {
  // `auto-fit` sizes its own tracks, so there is no count to fill against.
  if (columns === null || !Number.isFinite(columns) || columns < 1) return spans.map((s) => s ?? null);

  const cols = Math.floor(columns);
  // A tile with no span occupies the one column CSS grid gives it.
  const want = spans.map((span) => {
    const n = typeof span === 'number' && Number.isFinite(span) ? Math.floor(span) : 1;
    return Math.max(1, Math.min(n, cols));
  });

  const out = want.slice();
  let start = 0;
  let used = 0;
  const grow = (from: number, to: number) => {
    const row = want.slice(from, to);
    const total = row.reduce((a, b) => a + b, 0);
    if (total >= cols || !row.length) return;
    // Proportional, by largest remainder — so the shares stay as close to the
    // authored ratio as whole columns allow, and they add up exactly.
    const exact = row.map((span) => (span * cols) / total);
    const floors = exact.map((v) => Math.max(1, Math.floor(v)));
    let spare = cols - floors.reduce((a, b) => a + b, 0);
    const order = exact
      .map((v, i) => ({ i, rem: v - Math.floor(v) }))
      .sort((a, b) => b.rem - a.rem || a.i - b.i);
    for (let k = 0; spare > 0 && order.length; k = (k + 1) % order.length) {
      floors[order[k].i] += 1;
      spare -= 1;
    }
    for (let i = 0; i < row.length; i += 1) out[from + i] = Math.min(floors[i], cols);
  };

  for (let i = 0; i < want.length; i += 1) {
    if (used + want[i] > cols && used > 0) {
      grow(start, i);
      start = i;
      used = 0;
    }
    used += want[i];
  }
  grow(start, want.length);
  return out;
}
